import { ApiError } from '@/lib/api/api-client'

import { isValidUUID } from '@scrambl/shared'

/**
 * What a failed "Create draft invoice" means for the person looking at the
 * billing run. The run is one transaction server-side, so a clean rejection
 * created nothing — but a lost response may hide a run that DID succeed, and a
 * retry of that run comes back as a 409 because its own work is now invoiced.
 * Both of those need a way to the invoice, not just an error string.
 */
export type BillingRunFailure =
  /** 409: some selected work is already on these (live) invoices. */
  | {
      kind: 'already-invoiced'
      message: string
      invoices: { invoiceId: string; invoiceNumber: string | null }[]
    }
  /** 409 for another reason (retainer, unapproved, ...): nothing to link. */
  | { kind: 'ineligible'; message: string }
  /** No usable response: the run may or may not have been created. */
  | { kind: 'unknown-outcome'; message: string }
  /** The server answered and refused; nothing was created. */
  | { kind: 'rejected'; message: string }

const ALREADY_INVOICED_CODE = 'BILLING_RUN_ALREADY_INVOICED'

// Only known request refusals prove that the command did not commit. Server
// failures, redirects, deadlines and unrecognized statuses remain uncertain.
const REJECTION_STATUSES = new Set([
  400, 401, 403, 404, 405, 413, 415, 422, 429,
])

function readInvoices(
  data: unknown,
): { invoiceId: string; invoiceNumber: string | null }[] {
  if (!data || typeof data !== 'object') return []
  const invoices = (data as { invoices?: unknown }).invoices
  if (!Array.isArray(invoices)) return []
  return invoices.flatMap(item => {
    if (!item || typeof item !== 'object') return []
    const { invoiceId, invoiceNumber } = item as Record<string, unknown>
    if (typeof invoiceId !== 'string' || !isValidUUID(invoiceId)) return []
    return [
      {
        invoiceId,
        invoiceNumber:
          typeof invoiceNumber === 'string' && invoiceNumber.length <= 200
            ? invoiceNumber
            : null,
      },
    ]
  })
}

export function classifyBillingRunFailure(error: unknown): BillingRunFailure {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      const code =
        error.data && typeof error.data === 'object'
          ? (error.data as { code?: unknown }).code
          : undefined
      const invoices = readInvoices(error.data)
      if (code === ALREADY_INVOICED_CODE) {
        return {
          kind: 'already-invoiced',
          message:
            invoices.length === 1 && invoices[0]!.invoiceNumber
              ? `Some of this work is already on invoice ${invoices[0]!.invoiceNumber}. The billing run has been refreshed.`
              : 'Some of this work is already invoiced. The billing run has been refreshed.',
          invoices,
        }
      }
      return {
        kind: 'ineligible',
        message:
          'Some of the selected work can no longer be invoiced. The billing run has been refreshed — review it and try again.',
      }
    }
    if (REJECTION_STATUSES.has(error.status)) {
      return {
        kind: 'rejected',
        message: error.message || 'Failed to create invoices',
      }
    }
  }
  return {
    kind: 'unknown-outcome',
    message:
      'We couldn’t confirm whether the invoice was created. The billing run has been refreshed — check this client’s invoices before trying again.',
  }
}
